
import React, { useState, useEffect, useRef } from 'react';
import { Star, ArrowLeft, MapPin, Quote, ArrowRight, Flame, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import FaitImage from './FaitImage';
import FadeIn from './FadeIn';
import { useLanguage } from './LanguageContext';
import KosherStamp from './KosherStamp';
import SEO from './SEO';

const CountdownTimer: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<{days: number, hours: number, minutes: number, seconds: number} | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false); // Track state to manage transitions without stale closures
  const [animState, setAnimState] = useState<'idle' | 'exiting' | 'entering'>('idle');
  const { t } = useLanguage();

  useEffect(() => {
    // 1. Initial Check on Mount (No Animation)
    const now = new Date();
    const currentDay = now.getDay();
    const currentHour = now.getHours();

    // Logic: Open Thursday 13:00 (Day 4) until Friday 03:00 (Day 5)
    let initialOpen = false;
    if (currentDay === 4 && currentHour >= 13) initialOpen = true;
    if (currentDay === 5 && currentHour < 3) initialOpen = true;

    setIsOpen(initialOpen);
    isOpenRef.current = initialOpen;

    // 2. Interval Loop
    const calculateTime = () => {
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();

      // Check Real Open Status
      let isCurrentlyOpen = false;
      if (day === 4 && hour >= 13) isCurrentlyOpen = true;
      if (day === 5 && hour < 3) isCurrentlyOpen = true;

      // Handle Status Change (Animation)
      if (isCurrentlyOpen !== isOpenRef.current) {
          isOpenRef.current = isCurrentlyOpen;

          // Start Exit Animation
          setAnimState('exiting');
          
          setTimeout(() => {
              // Swap Content
              setIsOpen(isCurrentlyOpen);
              setAnimState('entering');
              
              // End Animation
              setTimeout(() => setAnimState('idle'), 700);
          }, 700);
      }

      // Calculate Numbers (Next Thursday 13:00)
      const nextOpen = new Date(now);
      nextOpen.setDate(now.getDate() + ((4 + 7 - now.getDay()) % 7));
      nextOpen.setHours(13, 0, 0, 0);

      // If calculation lands in the past (e.g., today is Thu 14:00), aim for next week
      if (nextOpen <= now) {
         nextOpen.setDate(nextOpen.getDate() + 7);
      }

      const difference = nextOpen.getTime() - now.getTime();

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      });
    };

    const timer = setInterval(calculateTime, 1000);
    calculateTime(); // Initial run

    return () => clearInterval(timer);
  }, []);

  if (!timeLeft) return null;

  // Animation Classes Logic
  const getContainerClass = () => {
     if (animState === 'exiting') return 'opacity-0 scale-90 blur-lg duration-700 ease-in';
     if (animState === 'entering') return 'opacity-0 scale-110 blur-sm duration-0'; // Start state for enter
     return 'opacity-100 scale-100 blur-0 duration-700 ease-out'; // Final state
  };

  // Immediate effect to trigger the "enter" transition after render
  if (animState === 'entering') {
      requestAnimationFrame(() => {
          setAnimState('idle'); 
      });
  }

  return (
    <div className={`transition-all transform min-h-[160px] flex items-center justify-center ${getContainerClass()}`}>
      {isOpen ? (
        // ------------------------------------------------------
        // NEW FRAMELESS "OPEN" DESIGN
        // ------------------------------------------------------
        <div className="relative text-center z-10">
          
          {/* 1. Ambient Light/Glow behind text */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-fait-gold/10 blur-[60px] rounded-full -z-10 animate-pulse-slow"></div>
          
          <div className="flex flex-col items-center">
            {/* Animated Icon */}
            <div className="mb-4 text-fait-gold drop-shadow-[0_0_15px_rgba(206,172,100,0.8)]">
                <Flame className="w-12 h-12 animate-pulse" fill="currentColor" />
            </div>

            {/* Main Text - Gradient Gold, Big, Bold */}
            <h2 className="text-5xl md:text-7xl font-serif font-black text-transparent bg-clip-text bg-gradient-to-b from-[#FFF5D6] via-[#CEAC64] to-[#9E8246] drop-shadow-[0_4px_10px_rgba(0,0,0,0.8)] tracking-tight">
              {t.home.openStatus}
            </h2>

            {/* Subtitle with lines */}
            <div className="flex items-center gap-4 mt-4 opacity-90">
                <div className="h-px w-12 bg-gradient-to-l from-fait-gold to-transparent"></div>
                <p className="text-white font-sans text-xl font-light tracking-wide drop-shadow-md">
                  {t.home.openMsg}
                </p>
                <div className="h-px w-12 bg-gradient-to-r from-fait-gold to-transparent"></div>
            </div>
          </div>
        </div>
      ) : (
        // ------------------------------------------------------
        // TIMER DESIGN
        // ------------------------------------------------------
        <div className="bg-black/40 border border-white/10 p-6 rounded-2xl backdrop-blur-md shadow-2xl w-full max-w-md">
           <h3 className="text-xl font-serif text-gray-300 mb-4 text-center tracking-widest uppercase text-xs">{t.home.opensIn}</h3>
           <div className="flex justify-center gap-2 md:gap-4 text-center">
              <div className="flex flex-col w-16 md:w-20">
                <span className="block text-4xl font-bold text-fait-gold font-serif leading-none filter drop-shadow-lg">{timeLeft.days}</span>
                <span className="text-xs text-gray-400 font-sans mt-1 uppercase tracking-wider">{t.home.days}</span>
              </div>
              <span className="text-2xl text-gray-600 font-serif pt-1">:</span>
              <div className="flex flex-col w-16 md:w-20">
                <span className="block text-4xl font-bold text-fait-gold font-serif leading-none filter drop-shadow-lg">{timeLeft.hours}</span>
                <span className="text-xs text-gray-400 font-sans mt-1 uppercase tracking-wider">{t.home.hours}</span>
              </div>
              <span className="text-2xl text-gray-600 font-serif pt-1">:</span>
               <div className="flex flex-col w-16 md:w-20">
                <span className="block text-4xl font-bold text-fait-gold font-serif leading-none filter drop-shadow-lg">{timeLeft.minutes}</span>
                <span className="text-xs text-gray-400 font-sans mt-1 uppercase tracking-wider">{t.home.minutes}</span>
              </div>
              <span className="text-2xl text-gray-600 font-serif pt-1">:</span>
               <div className="flex flex-col w-16 md:w-20">
                <span className="block text-4xl font-bold text-fait-gold font-serif leading-none filter drop-shadow-lg">{timeLeft.seconds}</span>
                <span className="text-xs text-gray-400 font-sans mt-1 uppercase tracking-wider">{t.home.seconds}</span>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const ReviewsSection: React.FC = () => {
  const { t, isRtl } = useLanguage();
  const reviews = t.home.reviews || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [withTransition, setWithTransition] = useState(true);

  // Extend reviews list for seamless looping
  const extendedReviews = [...reviews, ...reviews.slice(0, 3)];

  const handleNext = () => {
    if (!withTransition) return;
    setCurrentIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (!withTransition) return;
    if (currentIndex === 0) {
      setWithTransition(false);
      setCurrentIndex(reviews.length);
      setTimeout(() => {
        setWithTransition(true);
        setCurrentIndex(reviews.length - 1);
      }, 20);
    } else {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= reviews.length) {
      setWithTransition(false);
      setCurrentIndex(currentIndex % reviews.length);
      setTimeout(() => {
        setWithTransition(true);
      }, 20);
    }
  };

  useEffect(() => {
    if (isHovered || !reviews.length) return;
    const interval = setInterval(() => {
      handleNext();
    }, 4500);
    return () => clearInterval(interval);
  }, [isHovered, reviews.length, currentIndex, withTransition]);

  return (
    <section 
      className="py-24 bg-fait-brown/10 border-y border-fait-brown/20 relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute top-0 left-0 w-64 h-64 bg-fait-gold/5 rounded-full filter blur-3xl pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
        <FadeIn>
          <Quote className="w-12 h-12 text-fait-gold mx-auto mb-6 opacity-50 fill-current" />
          <h3 className="text-3xl font-serif font-bold text-fait-cream mb-2">{t.home.reviewsTitle}</h3>
          <p className="text-fait-gold text-sm font-semibold mb-12">
            {isRtl ? 'חוות דעת גוגל' : 'Google Reviews'}
          </p>
        </FadeIn>
        
        <div className="relative overflow-hidden px-1 py-4" dir="ltr">
          <div 
            onTransitionEnd={handleTransitionEnd}
            style={{
              transform: `translateX(calc(-${currentIndex} * (100% + 1.5rem) / var(--items-per-view, 1)))`,
            }}
            className={`flex gap-6 [--items-per-view:1] md:[--items-per-view:3] ${
              withTransition ? 'transition-transform duration-700 ease-in-out' : ''
            }`}
          >
            {extendedReviews.map((review, index) => (
              <div 
                key={`${review.id}-${index}`} 
                dir={isRtl ? 'rtl' : 'ltr'}
                className="w-full md:w-[calc((100%-3rem)/3)] flex-shrink-0 bg-[#1a120b] p-8 rounded-xl shadow-lg border border-white/10 hover:border-fait-gold/40 transition-all duration-300 flex flex-col justify-between text-right rtl:text-right ltr:text-left min-h-[260px] group"
              >
                <div>
                  <div className="flex text-fait-gold gap-1 mb-4 justify-start">
                    {[...Array(review.stars)].map((_, i) => (
                      <Star key={i} fill="currentColor" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-200 italic mb-6 text-base font-serif leading-relaxed">
                    "{review.text}"
                  </p>
                </div>
                <div className="border-t border-white/10 pt-4 mt-auto">
                  <p className="text-white font-bold text-lg">{review.name}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-6 mt-10" dir="ltr">
            {/* Left Button (Left side): brings cards from left */}
            <button
              onClick={handlePrev}
              className="p-3 rounded-full bg-black/40 border border-fait-gold/30 text-fait-gold hover:bg-fait-gold hover:text-fait-dark transition-all duration-300 shadow-lg cursor-pointer"
              aria-label="Slide Left"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-2 items-center">
              {reviews.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setWithTransition(true);
                    setCurrentIndex(idx);
                  }}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    (currentIndex % reviews.length) === idx 
                      ? 'w-8 h-2.5 bg-fait-gold' 
                      : 'w-2.5 h-2.5 bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* Right Button (Right side): brings cards from right */}
            <button
              onClick={handleNext}
              className="p-3 rounded-full bg-black/40 border border-fait-gold/30 text-fait-gold hover:bg-fait-gold hover:text-fait-dark transition-all duration-300 shadow-lg cursor-pointer"
              aria-label="Slide Right"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
        
        <FadeIn delay={200} className="mt-8">
          <p className="text-gray-400 text-sm">{t.home.reviewsCount}</p>
        </FadeIn>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  const { t, isRtl, language } = useLanguage();
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

  return (
    <div className="w-full overflow-x-hidden">
      <SEO 
        title={language === 'he' ? 'ראשי' : 'Home'} 
        description={t.home.teaserText}
        schema={{
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "150"
          }
        }}
      />
      
      {/* 1. HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-12">
        <div className="absolute inset-0 z-0 transform scale-105">
            <FaitImage 
              src="/images/hero-cholent.jpg" 
              alt="Hero Background"
              className="w-full h-full object-cover" // Removed brightness filters
              imageContext="cholent"
            />
        </div>
        
        {/* Stronger gradient overlay to ensure text readability against bright food */}
        <div className="absolute inset-0 bg-gradient-to-b from-fait-dark/70 via-black/20 to-fait-dark z-0"></div>
        <div className="absolute inset-0 bg-black/20 z-0"></div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto flex flex-col items-center">
          
          <FadeIn direction="up" delay={100}>
            <h1 className="text-6xl md:text-9xl font-serif font-bold text-fait-gold mb-4 tracking-tight drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)]">
              {t.home.heroTitle}
            </h1>
          </FadeIn>
          
          <FadeIn direction="up" delay={200}>
            <h2 className="text-2xl md:text-4xl text-white font-serif font-medium mb-12 drop-shadow-md">
              {t.home.heroSubtitle}
            </h2>
          </FadeIn>

          {/* TIMER AREA */}
          <div className="mb-16 w-full flex justify-center h-40 items-center">
             <CountdownTimer />
          </div>

          <FadeIn direction="up" delay={400}>
            <div className="flex flex-col sm:flex-row gap-6 w-full sm:w-auto">
              <Link 
                to="/menu"
                className="bg-fait-gold text-fait-dark hover:bg-[#DBC17F] font-bold py-4 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-105 shadow-xl shadow-amber-900/30 text-center flex items-center justify-center gap-2"
              >
                {t.home.heroCtaMenu} <ArrowIcon size={20} />
              </Link>
              <Link 
                to="/contact"
                className="bg-black/40 border-2 border-fait-gold/60 text-white hover:border-fait-gold hover:text-fait-gold font-bold py-4 px-12 rounded-lg text-xl transition-all duration-300 text-center backdrop-blur-md flex items-center justify-center gap-2 shadow-lg"
              >
                 {t.home.heroCtaLocation} <MapPin size={20} />
              </Link>
            </div>
          </FadeIn>
        </div>

        {/* Authentic Kosher Stamp */}
        <div className="absolute bottom-8 left-8 z-20 opacity-90 animate-fade-in hidden xl:block pointer-events-none drop-shadow-lg">
             <KosherStamp className="w-56 h-36 text-fait-gold rotate-[-8deg]" />
        </div>
      </section>

      {/* 2. THE EXPERIENCE TEASER */}
      <section className="py-24 bg-fait-dark relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 relative h-[500px]">
              <FadeIn direction="right" delay={200} className="h-full w-full">
                <div className="absolute -inset-4 bg-fait-gold/10 rounded-2xl rotate-3"></div>
                <FaitImage 
                  src="/images/atmosphere-interior.jpg" 
                  alt="Jewish Food Atmosphere" 
                  className="w-full h-full object-cover rounded-2xl shadow-2xl relative z-10 transition-all duration-700 hover:scale-[1.02]" // Removed grayscale
                  imageContext="interior"
                />
              </FadeIn>
            </div>
            <div className="order-1 md:order-2 text-right rtl:text-right ltr:text-left">
              <FadeIn direction="left" delay={200}>
                <span className="text-fait-gold font-bold tracking-widest text-sm uppercase mb-2 block">{t.home.teaserOpen}</span>
                <h3 className="text-4xl md:text-6xl font-serif font-bold text-fait-cream mb-6 leading-tight whitespace-pre-line">
                  {t.home.teaserTitle}
                </h3>
                <p className="text-gray-200 text-lg leading-relaxed mb-8 font-sans">
                  {t.home.teaserText}
                </p>
                <div className="flex gap-8 border-t border-white/10 pt-8">
                   <div>
                      <span className="block text-4xl font-bold text-fait-gold font-serif">4.9</span>
                      <span className="text-sm text-gray-400">{t.home.statsStars}</span>
                   </div>
                   <div>
                      <span className="block text-4xl font-bold text-fait-gold font-serif">100%</span>
                      <span className="text-sm text-gray-400">{t.home.statsService}</span>
                   </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FAVORITES PREVIEW */}
      <section className="py-24 bg-[#140f0b]">
         <div className="max-w-7xl mx-auto px-6 text-center">
            <FadeIn>
              <h3 className="text-3xl md:text-5xl font-serif font-bold text-fait-cream mb-16">
                 {t.home.favoritesTitle}
              </h3>
            </FadeIn>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               {/* Card 1 */}
               <FadeIn delay={100} className="h-full">
                 <Link to="/menu" className="group block h-full">
                    <div className="relative h-80 rounded-2xl overflow-hidden mb-4 shadow-xl">
                       {/* Gradient only at the bottom for text readability */}
                       <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 to-transparent z-10"></div>
                       <FaitImage 
                          src="/images/hero-cholent.jpg" 
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                          alt="Cholent"
                          imageContext="cholent" 
                       />
                       <div className="absolute bottom-6 right-6 rtl:right-6 rtl:left-auto ltr:left-6 ltr:right-auto z-20 text-right rtl:text-right ltr:text-left">
                          <h4 className="text-2xl font-serif font-bold text-white drop-shadow-sm">{t.home.favCholent}</h4>
                          <span className="text-fait-gold font-bold">{t.home.favCholentSub}</span>
                       </div>
                    </div>
                 </Link>
               </FadeIn>

               {/* Card 2 - KUGEL */}
               <FadeIn delay={200} className="h-full">
                 <Link to="/menu" className="group block h-full">
                    <div className="relative h-80 rounded-2xl overflow-hidden mb-4 shadow-xl">
                       <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 to-transparent z-10"></div>
                       <FaitImage 
                          src="/images/kugel-yerushalmi.jpg"
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                          alt="Kugel"
                          imageContext="kugel" 
                       />
                       <div className="absolute bottom-6 right-6 rtl:right-6 rtl:left-auto ltr:left-6 ltr:right-auto z-20 text-right rtl:text-right ltr:text-left">
                          <h4 className="text-2xl font-serif font-bold text-white drop-shadow-sm">{t.home.favKugel}</h4>
                          <span className="text-fait-gold font-bold">{t.home.favKugelSub}</span>
                       </div>
                    </div>
                 </Link>
               </FadeIn>

               {/* Card 3 - KISHKE */}
               <FadeIn delay={300} className="h-full">
                 <Link to="/menu" className="group block h-full">
                    <div className="relative h-80 rounded-2xl overflow-hidden mb-4 shadow-xl">
                       <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 to-transparent z-10"></div>
                       <FaitImage 
                          src="/images/cholent-plate.jpg" 
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                          alt="Kishke"
                          imageContext="kishke" 
                       />
                       <div className="absolute bottom-6 right-6 rtl:right-6 rtl:left-auto ltr:left-6 ltr:right-auto z-20 text-right rtl:text-right ltr:text-left">
                          <h4 className="text-2xl font-serif font-bold text-white drop-shadow-sm">{t.home.favKishke}</h4>
                          <span className="text-fait-gold font-bold">{t.home.favKishkeSub}</span>
                       </div>
                    </div>
                 </Link>
               </FadeIn>
            </div>

            <FadeIn delay={400} className="mt-12">
               <Link to="/menu" className="inline-block border border-fait-gold text-fait-gold px-8 py-3 rounded-full hover:bg-fait-gold hover:text-fait-dark transition-all duration-300 font-bold">
                  {t.home.fullMenuBtn}
               </Link>
            </FadeIn>
         </div>
      </section>

      {/* 4. REVIEWS / SOCIAL PROOF */}
      <ReviewsSection />

      {/* 5. BIG CTA */}
      <section className="py-32 relative">
         <div className="absolute inset-0 z-0">
             <FaitImage 
                src="/images/quiet-corner.jpg"
                alt="Background" 
                className="w-full h-full object-cover"
                imageContext="crowd"
             />
         </div>
         <div className="absolute inset-0 bg-black/60 z-0"></div>
         
         <div className="relative z-10 text-center px-4">
            <FadeIn direction="up">
              <h2 className="text-4xl md:text-7xl font-serif font-bold text-white mb-6 drop-shadow-lg">
                 {t.home.finalCtaTitle}
              </h2>
            </FadeIn>
            <FadeIn direction="up" delay={100}>
              <p className="text-xl md:text-2xl text-gray-100 mb-10 font-light drop-shadow-md">
                 {t.home.finalCtaText}
              </p>
            </FadeIn>
            <FadeIn direction="up" delay={200}>
              <Link to="/contact" className="bg-fait-gold text-fait-dark font-bold text-xl py-4 px-10 rounded-lg hover:bg-white hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(206,172,100,0.5)]">
                 {t.home.finalCtaBtn}
              </Link>
            </FadeIn>
         </div>
      </section>

    </div>
  );
};

export default Home;
